import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  InputLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  QueryClient,
  QueryClientProvider,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import {
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useDispatch } from 'store';
import {
  deleteUser,
  downloadUsersCSV,
  getUsersList,
  postUser,
  putUser,
  request2FA,
  uploadUsersCSV,
  verify2FA,
} from 'store/slices/users';
import LightButton from 'ui-component/button/LightButton';
import PrimaryButton from 'ui-component/button/PrimaryButton';
import AnimateButton from 'ui-component/extended/AnimateButton';
import Loader from 'ui-component/Loader';
import DropzoneContainer from 'views/content/backTests/DropzoneContainer';

const statusOptions = [
  { label: 'Active', value: 'Active' },
  { label: 'Inactive', value: 'Inactive' },
];

// Helper function to check if phone number is complete (not just country code)
const isPhoneComplete = (phone) => {
  if (!phone || !phone.trim()) return false;
  // Remove + and check if there are at least 10 digits (country code + phone number)
  // Minimum: country code (1-3 digits) + phone number (at least 7 digits) = at least 10 digits total
  const digitsOnly = phone.replace(/\D/g, '');
  return digitsOnly.length >= 10;
};

// Helper function to validate E.164 phone format
const validateE164Phone = (phone) => {
  if (!phone || !phone.trim()) return true; // Phone is optional
  // E.164 format: + followed by 1-15 digits
  const e164Regex = /^\+[1-9]\d{1,14}$/;
  if (!e164Regex.test(phone)) {
    return false;
  }
  // Check if phone is complete (not just country code)
  return isPhoneComplete(phone);
};

// Helper function to format phone number to E.164
const formatPhoneToE164 = (phone) => {
  if (!phone) return '';
  // Remove all non-digit characters except +
  let cleaned = phone.replace(/[^\d+]/g, '');
  
  // Default country code is +91 for all countries
  // If it doesn't start with +, add default country code +91
  if (!cleaned.startsWith('+')) {
    // If it starts with 0, remove it
    if (cleaned.startsWith('0')) {
      cleaned = cleaned.substring(1);
    }
    // Add +91 as default country code for all users
    cleaned = '+91' + cleaned;
  }
  
  return cleaned;
};

const UsersTable = () => {
  const dispatch = useDispatch();
  const username = localStorage.getItem("userName")
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openImportDialog, setOpenImportDialog] = useState(false);
  const [open2FADialog, setOpen2FADialog] = useState(false);
  const [otp, setOtp] = useState('');
  const [isRequesting2FA, setIsRequesting2FA] = useState(false);
  const [isVerifying2FA, setIsVerifying2FA] = useState(false);
  const [selectedUserFor2FA, setSelectedUserFor2FA] = useState(null);
  const [isEnabling2FA, setIsEnabling2FA] = useState(true);

  const [selectedRow, setSelectedRow] = useState(null);
  const [usersData, setUsersData] = useState([]);

  const [file, setFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [newUser, setNewUser] = useState({
    username: '',
    full_name: '',
    email: '',
    phone: '',
    password: '',
    disabled: false,
  });
  const [errors, setErrors] = useState({});

  // ✅ Fetch Users
  const fetchData = async () => {
    const response = await dispatch(getUsersList());
    return response;
  };

  const {
    data: users = [],
    refetch,
    isLoading,
  } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await fetchData();
      return response.data;
    },
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    setUsersData(users?.data?.slice()?.reverse());
  }, [users]);

  // ✅ 2FA Handlers (defined before columns to use in columns)
  const handle2FAToggle = useCallback(async (user, enabled) => {
    // Both enabling and disabling require OTP verification
    setSelectedUserFor2FA(user);
    setIsEnabling2FA(enabled);
    setOpen2FADialog(true);
    setOtp('');
    setIsRequesting2FA(true);
    
    try {
      // Request 2FA with enabled flag (true for enabling, false for disabling)
      await dispatch(request2FA(user.username, { enabled }));
      toast.success('OTP sent successfully');
    } catch (error) {
      toast.error('Failed to request 2FA');
      setOpen2FADialog(false);
    } finally {
      setIsRequesting2FA(false);
    }
  }, [dispatch]);

  // ✅ Table Columns
  const columns = useMemo(
    () => [
      { accessorKey: 'username', header: 'User Name' },
      { accessorKey: 'full_name', header: 'Full Name' },
      { accessorKey: 'email', header: 'Email' },
      {
        accessorKey: 'disabled',
        header: 'Status',
        Cell: ({ cell }) => (
          <span
            style={{
              color: cell.getValue() === false ? 'green' : 'red',
              fontWeight: 500,
            }}
          >
            {cell.getValue() === false ? 'Active' : 'Inactive'}
          </span>
        ),
      },
      {
        id: '2fa',
        header: 'Two Factor Authentication',
        Cell: ({ row }) => {
          if (!username) return null;
          const isLoggedInUser = row.original?.username === username;
          if (!isLoggedInUser) return null;
          return (
            <Switch
              checked={row.original?.two_fa_enabled || false}
              onChange={(e) => handle2FAToggle(row.original, e.target.checked)}
              disabled={isRequesting2FA || isVerifying2FA}
            />
          );
        },
      },
    ],
    [username, isRequesting2FA, isVerifying2FA, handle2FAToggle]
  );

  // ✅ Mutations
  const createUser = useMutation({
    mutationFn: async (payload) => dispatch(postUser(payload)),
    onSuccess: () => {
      toast.success('User added successfully');
      refetch();
    },
    onError: () => toast.error('Failed to add user'),
  });

  const updateUser = useMutation({
    mutationFn: async (payload) => dispatch(putUser(payload)),
    onSuccess: () => {
      toast.success('User updated successfully');
      refetch();
    },
    onError: () => toast.error('Failed to update user'),
  });

  const removeUser = useMutation({
    mutationFn: async (userId) => dispatch(deleteUser({ userId })),
    onSuccess: () => {
      toast.success('User deleted successfully');
      refetch();
    },
    onError: () => toast.error('Failed to delete user'),
  });

  // ✅ Handlers
  const handleOpenAddDialog = () => {
    setNewUser({
      username: '',
      full_name: '',
      email: '',
      phone: '',
      password: '',
      disabled: false,
    });
    setSelectedRow(null);
    setErrors({});
    setOpenEditDialog(true);
  };

  const handleOpenEditDialog = (row) => {
    setSelectedRow(row);
    setErrors({});
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setSelectedRow(null);
    setErrors({});
  };

  const handleOpenDeleteDialog = (row) => {
    setSelectedRow(row);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedRow(null);
  };

  const handleInputChange = (field, value) => {
    // Format phone number to E.164 format (only if value is provided)
    if (field === 'phone') {
      if (value && value.trim()) {
        value = formatPhoneToE164(value);
        // Only validate if user has entered something meaningful (more than just + or country code)
        // If it's incomplete, show error. If empty, clear error.
        if (value.length > 3 && !isPhoneComplete(value)) {
          // User has entered something but it's incomplete
          setErrors((prev) => ({ ...prev, phone: 'Please enter a complete phone number (e.g. +919876543210)' }));
        } else if (value.length > 3 && !validateE164Phone(value)) {
          // User has entered something but format is invalid
          setErrors((prev) => ({ ...prev, phone: 'Phone must be in E.164 format (e.g. +919876543210)' }));
        } else {
          // Phone is valid or user is still typing (length <= 3 means just country code, allow them to continue)
          setErrors((prev) => ({ ...prev, phone: '' }));
        }
      } else {
        // Phone is empty - clear error since it's optional
        value = '';
        setErrors((prev) => ({ ...prev, phone: '' }));
      }
    }
    
    if (selectedRow) {
      setSelectedRow((prev) => ({
        ...prev,
        original: { ...prev.original, [field]: value },
      }));
    } else {
      setNewUser((prev) => ({ ...prev, [field]: value }));
    }
    
    // Clear error when user starts typing (for other fields)
    if (errors[field] && field !== 'phone') {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

const validateUser = (user, selectedRow) => {
  const validationErrors = {};
  if (!user.username) validationErrors.username = 'Username is required';
  if (!user.email) validationErrors.email = 'Email is required';
  if (!selectedRow && !user.password) {
    validationErrors.password = 'Password is required';
  }
  // Only validate phone if user has entered something (phone is optional)
  if (user.phone && user.phone.trim() && user.phone.trim().length > 3) {
    // Check if phone is just country code or incomplete
    if (!isPhoneComplete(user.phone)) {
      validationErrors.phone = 'Please enter a complete phone number (e.g. +919876543210)';
    } else if (!validateE164Phone(user.phone)) {
      validationErrors.phone = 'Phone must be in E.164 format (e.g. +919876543210)';
    }
  }
  // If phone is empty or just country code (length <= 3), no error since it's optional
  return validationErrors;
};

  const handleSaveUser = async () => {
    const userData = selectedRow ? selectedRow.original : newUser;
    
    // Format phone number to E.164 before validation (only if provided)
    if (userData.phone && userData.phone.trim()) {
      userData.phone = formatPhoneToE164(userData.phone);
    }
    
    const validationErrors = validateUser(userData, selectedRow);
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }

    if (!selectedRow && userData.password) {
      // Ensure disabled is false for new users
      const newUserPayload = { ...userData, disabled: false };
      // Remove phone field if it's empty, incomplete, or just country code
      if (!newUserPayload.phone || !newUserPayload.phone.trim() || !isPhoneComplete(newUserPayload.phone)) {
        delete newUserPayload.phone;
      }
      await createUser.mutateAsync(newUserPayload);
    } else {
      const payload = { ...userData };
      if (!userData.password) delete payload.password;
      // Remove phone field if it's empty, incomplete, or just country code
      if (!payload.phone || !payload.phone.trim() || !isPhoneComplete(payload.phone)) {
        delete payload.phone;
      }
      await updateUser.mutateAsync(payload);
    }
    handleCloseEditDialog();
  };


  const handleDeleteUser = async () => {
    await removeUser.mutateAsync(selectedRow.original.username);
    handleCloseDeleteDialog();
  };

  const handleUploadCSV = () => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    dispatch(uploadUsersCSV(formData))
      .then(() => {
        setFile(null);
        setOpenImportDialog(false);
        toast.success('Users imported successfully');
        refetch();
      })
      .catch(() => toast.error('Error uploading file'));
  };

  const handleClickDownload = () => {
    dispatch(downloadUsersCSV())
      .then((res) => {
        const csvData = res.data;
        const blob = new Blob([csvData], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `users.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success('Users downloaded successfully');
      })
      .catch(() => toast.error('Error downloading file'));
  };


  const handleVerify2FA = async () => {
    if (!otp || otp.length < 4) {
      toast.error('Please enter a valid OTP');
      return;
    }

    if (!selectedUserFor2FA) {
      toast.error('User information is missing');
      return;
    }

    setIsVerifying2FA(true);
    try {
      // Verify OTP
      await dispatch(verify2FA(selectedUserFor2FA.username, otp));
      
      // Update user with two_fa_enabled based on isEnabling2FA flag
      const payload = {
        username: selectedUserFor2FA.username,
        full_name: selectedUserFor2FA.full_name,
        email: selectedUserFor2FA.email,
        phone: selectedUserFor2FA.phone || '',
        disabled: selectedUserFor2FA.disabled,
        two_fa_enabled: isEnabling2FA,
      };
      await dispatch(putUser(payload));
      
      toast.success(
        isEnabling2FA
          ? 'Two Factor Authentication enabled successfully'
          : 'Two Factor Authentication disabled successfully'
      );
      setOpen2FADialog(false);
      setOtp('');
      setSelectedUserFor2FA(null);
      refetch();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to verify OTP');
    } finally {
      setIsVerifying2FA(false);
    }
  };

  const handleClose2FADialog = () => {
    setOpen2FADialog(false);
    setOtp('');
    setSelectedUserFor2FA(null);
    setIsEnabling2FA(true);
  };
  // ✅ Table Setup
  const table = useMaterialReactTable({
    columns,
    data: usersData || [],
    getRowId: (row) => row.username,
    positionActionsColumn: 'last',
    enableRowActions: true,
    renderRowActions: ({ row }) => {
      if (!username) return null;
      return (
        <Box sx={{ display: 'flex', gap: '1rem' }}>
          {row?.original?.username !== username && (
            <>
              <Tooltip title="Edit">
                <IconButton onClick={() => handleOpenEditDialog(row)}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  color="error"
                  onClick={() => handleOpenDeleteDialog(row)}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      );
    },
    renderTopToolbarCustomActions: () => (
      <AnimateButton>
        <LightButton
          startIcon={<AddIcon />}
          onClick={handleOpenAddDialog}
          title="Add User"
        />
      </AnimateButton>
    ),
  });

  if (isLoading) return <Loader />;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <MaterialReactTable table={table} />
      </Grid>

      {/* Delete Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this user?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDeleteDialog}
            color="info"
            variant="contained"
            sx={{ color: 'white' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteUser}
            color="error"
            sx={{ color: 'white' }}
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Dialog */}
      <Dialog
        open={openEditDialog}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{selectedRow ? 'Edit User' : 'Add User'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <InputLabel>Username</InputLabel>
              <TextField
                fullWidth
                placeholder="Enter username"
                value={selectedRow?.original.username || newUser.username}
                onChange={(e) => handleInputChange('username', e.target.value)}
                error={Boolean(errors.username)}
                helperText={errors.username}
                disabled={!!selectedRow} // can't edit username once created
              />
            </Grid>

            <Grid item xs={12}>
              <InputLabel>Full Name</InputLabel>
              <TextField
                fullWidth
                placeholder="Enter full name"
                value={selectedRow?.original.full_name || newUser.full_name}
                onChange={(e) => handleInputChange('full_name', e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <InputLabel>Email</InputLabel>
              <TextField
                fullWidth
                placeholder="Enter email"
                value={selectedRow?.original.email || newUser.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                error={Boolean(errors.email)}
                helperText={errors.email}
              />
            </Grid>

            <Grid item xs={12}>
              <InputLabel>Phone (Optional)</InputLabel>
              <TextField
                fullWidth
                placeholder="Enter phone number (e.g. +919876543210 or 9876543210)"
                value={selectedRow?.original.phone || newUser.phone || ''}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                error={Boolean(errors.phone)}
                helperText={errors.phone || 'Phone is optional. If provided, must be in E.164 format (e.g. +919876543210)'}
              />
            </Grid>

            {/* 🔑 Password field */}
            <Grid item xs={12}>
              <InputLabel>Password</InputLabel>
              <TextField
                fullWidth
                type="password"
                placeholder={
                  selectedRow
                    ? 'Enter new password (optional)'
                    : 'Enter password'
                }
                onChange={(e) => handleInputChange('password', e.target.value)}
                error={Boolean(errors.password)}
                helperText={errors.password}
              />
            </Grid>

            <Grid item xs={12}>
              <InputLabel>Status</InputLabel>
              <Autocomplete
                fullWidth
                options={statusOptions}
                getOptionLabel={(option) => option.label}
                value={statusOptions.find(
                  (opt) => {
                    const currentDisabled = selectedRow 
                      ? selectedRow.original.disabled 
                      : newUser.disabled;
                    return opt.value === (currentDisabled === false ? 'Active' : 'Inactive');
                  }
                )}
                onChange={(event, newValue) =>
                  handleInputChange('disabled', newValue?.value === 'Inactive')
                }
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select Status" />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseEditDialog}
            color="inherit"
            variant="contained"
          >
            Cancel
          </Button>
          <PrimaryButton
            onClick={handleSaveUser}
            title={selectedRow ? 'Save User' : 'Add User'}
          />
        </DialogActions>
      </Dialog>

      {/* Import Users Dialog */}
      <Dialog
        open={openImportDialog}
        onClose={() => setOpenImportDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography>Upload Users CSV</Typography>
            <IconButton onClick={() => setOpenImportDialog(false)}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <DropzoneContainer
            file={file}
            setFile={setFile}
            uploadProgress={uploadProgress}
            setUploadProgress={setUploadProgress}
            buttonName="Upload CSV"
            title="Drag & drop a CSV file here"
          />
        </DialogContent>
        <DialogActions>
          <PrimaryButton
            title="Save"
            onClick={handleUploadCSV}
            disabled={!file}
          />
          </DialogActions>
        </Dialog>

        {/* 2FA Dialog */}
        <Dialog
          open={open2FADialog}
          onClose={handleClose2FADialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h6">
                {isEnabling2FA
                  ? 'Enable Two Factor Authentication'
                  : 'Disable Two Factor Authentication'}
              </Typography>
              <IconButton onClick={handleClose2FADialog}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                An OTP has been sent to your email ({selectedUserFor2FA?.email || 'your registered email'}). Please enter the OTP to{' '}
                {isEnabling2FA ? 'enable' : 'disable'} Two Factor Authentication.
              </Typography>
              <TextField
                fullWidth
                label="Enter OTP"
                placeholder="Enter 6-digit OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputProps={{ maxLength: 6 }}
                disabled={isVerifying2FA}
                error={otp.length > 0 && otp.length < 4}
                helperText={
                  otp.length > 0 && otp.length < 4
                    ? 'OTP must be at least 4 digits'
                    : ''
                }
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={handleClose2FADialog}
              color="inherit"
              disabled={isVerifying2FA}
            >
              Cancel
            </Button>
            <PrimaryButton
              title={isEnabling2FA ? 'Verify & Enable' : 'Verify & Disable'}
              onClick={handleVerify2FA}
              disabled={!otp || otp.length < 4 || isVerifying2FA}
            />
          </DialogActions>
        </Dialog>
      </Grid>
    );
  };

const queryClient = new QueryClient();

const Users = () => (
  <Box p={2}>
    <QueryClientProvider client={queryClient}>
      <UsersTable />
    </QueryClientProvider>
  </Box>
);

export default Users;
